import { useMemo } from 'react';
import { booleanOptions, Filter } from '@fabric-ui/components';
import { AsyncGetOptions, OptionQueryType } from '@fabric-ui/components/Table';
import { emptyArray, uniqBy } from '@fabric-ui/utils';
import { keepPreviousData } from '@tanstack/react-query';
import { EntityProjectionName } from '@utils/entityProjectionNames';
import { useAsyncFilterOptions } from '../../../hooks/useAsyncFilterOptions';
import { cleanEmptyFilters, FieldFilterModeType, filterToExpression, isListFilterMode, sortBySeverity } from '../../../utils/filterUtils';
import { FILTER_FIELD_LABEL_MAPPINGS, FINDING_STATE_LABELS } from '../../../utils/filterValueLabels';
import { generateOption, orderByKey, sortByStringField } from '../../../utils/utils';
import { DwQueryRequest, SortDir } from '../../../views/CustomDashboards/types';
import { entitySearchFields } from '../../../views/Tickets/ticket.types';
import { useBuildWhereClause } from '../../../views/Tickets/useBuildWhereClause';
import { useExpressionBuilder } from '../ExpressionBuilderContext';
import { useFilterConfig, useFilterContext } from '../FilterContext';
import { FieldFilterValue, FilterOption } from '../types';

const findingStateOptions = Object.entries(FINDING_STATE_LABELS).map(([value, title]) => ({ title, value }));

interface UseFieldFilterOptionsParams {
  field: string;
  filters: Record<string, unknown>;
  isEnabled: boolean;
  mode: FieldFilterModeType;
  open: boolean;
  isDateField: boolean;
  isBooleanField: boolean;
  isNumberField: boolean;
  isSchemaField: boolean;
  supportedType: boolean;
  isGlobalFilter: boolean;
  activeProjName: string | undefined;
  value: FieldFilterValue | unknown[];
  search: string;
  debouncedValue: string;
  escapedValue: string;
  searchCondition: string | undefined;
  options: FilterOption[] | undefined;
  overrideExtra: string[] | undefined;
  extraOperands: Filter[] | undefined;
  extraOptions: string[] | undefined;
  label: React.ReactNode;
  getOptions: (data: FilterOption[], label: React.ReactNode, getValue?: (v: unknown) => React.ReactNode) => FilterOption[];
  getValue: ((v: unknown) => React.ReactNode) | undefined;
  queryKey: string | undefined;
  usage: 'SPF' | undefined;
  asyncGetOptions: AsyncGetOptions | undefined;
}

export const useFieldFilterOptions = ({
  field,
  filters,
  isEnabled,
  mode,
  open,
  isDateField,
  isBooleanField,
  isNumberField,
  isSchemaField,
  supportedType,
  isGlobalFilter,
  activeProjName: activeProjNameProp,
  value,
  search,
  debouncedValue,
  escapedValue,
  searchCondition,
  options,
  overrideExtra,
  extraOperands,
  extraOptions,
  label,
  getOptions,
  getValue,
  queryKey,
  usage,
  asyncGetOptions,
}: UseFieldFilterOptionsParams) => {
  const { enableQueryObj, activeProjName: contextActiveProjName, isFilterForIngressValues: isIngressField } = useFilterConfig();
  const { timeRange, excludeNullFromList } = useExpressionBuilder();
  const { accountEntities, measurements } = useFilterContext();
  const { aggProjs = {}, fieldMap = {} } = accountEntities ?? {};
  const activeProjName = activeProjNameProp ?? contextActiveProjName ?? fieldMap[field]?.mainProjId?.name;

  const fieldQuery = (() => {
    const fq = isGlobalFilter ? `${activeProjName}.${field.split('.').slice(1).join('.')}` : field;
    const explodeField = fieldMap?.[field]?.repeated ? `EXPLODE(${fq})` : fq;
    return isIngressField && activeProjName
      ? explodeField.replace(aggProjs[activeProjName].pathAlias, `${aggProjs[activeProjName]?.pathAlias}.sources`)
      : explodeField;
  })();

  const builtWhereClause = useBuildWhereClause({
    filters: isGlobalFilter ? { [field]: [] } : { ...filters, [field]: [] },
    // @ts-ignore
    extra: overrideExtra ? [...overrideExtra, searchCondition] : searchCondition,
    entityTypeId: (isGlobalFilter ? fieldMap[field] : activeProjName ? aggProjs[activeProjName]?.entityTypeId : undefined)?.name,
    fieldMap,
    searchFields: activeProjName ? entitySearchFields[activeProjName as EntityProjectionName] : undefined,
  });
  const whereClause = isSchemaField && activeProjName ? builtWhereClause : '';

  const sql = `SELECT distinct ${fieldQuery} as value
    FROM ${activeProjName}
    ${whereClause} ORDER BY value ASC limit 800`;

  const entityName = activeProjName ? aggProjs[activeProjName]?.entityTypeId?.name : undefined;
  const measurementsList = useMemo(
    () =>
      activeProjName && entityName
        ? sortByStringField(
            measurements.visible.filter(metric => metric.expression && metric.context.includes(entityName)),
            'displayName'
          )
        : [],
    [activeProjName, entityName, measurements.visible]
  );

  const typeMap = useMemo(
    () => measurementsList.reduce((acc, { systemName, type }) => ({ ...acc, [systemName]: type }), aggProjs[activeProjName ?? '']?.typeMap),
    [measurementsList, activeProjName]
  );

  const fieldQueryObj = useMemo(() => {
    const filtersObj = typeMap
      ? cleanEmptyFilters(filterToExpression(typeMap, isGlobalFilter ? { [field]: [] } : { ...filters, [field]: [] }))
      : undefined;
    return {
      select: { dims: [{ name: fieldQuery, alias: 'value' }], metrics: [] },
      filter:
        activeProjName && (escapedValue || filtersObj || extraOperands)
          ? {
              and: {
                operands: [
                  ...(escapedValue ? [{ expression: { fieldName: 'value', stringCondition: { contains: escapedValue } } }] : []),
                  ...(filtersObj ? [filtersObj] : []),
                  ...(extraOperands || []),
                ],
              },
            }
          : undefined,
      top: { size: 800, groupOthers: false },
      sorting: [{ name: 'value', dir: SortDir.ASC }],
      ...(activeProjName ? { projectionId: aggProjs[activeProjName]?.projId ?? { name: activeProjName, builtIn: true } } : {}),
      distinct: true,
      ...(timeRange && timeRange.length === 2 ? { timeRange: { between: { values: timeRange } } } : {}),
    };
  }, [fieldQuery, activeProjName, typeMap, isGlobalFilter, filters, escapedValue, extraOperands, timeRange]);

  const queryObjectOptions = {
    options: {
      type: OptionQueryType.Object,
      queryObject: fieldQueryObj as DwQueryRequest,
      debounceQueries: false,
      onSuccess: data => sortBySeverity({ data, severityFieldName: fieldQuery, key: 'value' }),
      totalRowCount: true,
    },
    isEnabled:
      isEnabled &&
      isListFilterMode(mode) &&
      open &&
      !isDateField &&
      !isBooleanField &&
      supportedType &&
      !isNumberField &&
      isSchemaField &&
      enableQueryObj,
    placeholderData: keepPreviousData,
  };

  const querySqlOptions = {
    options: {
      type: OptionQueryType.SQL,
      key: queryKey ?? sql,
      sql,
      debounceQueries: false,
      placeholderData: keepPreviousData,
      onSuccess: data => sortBySeverity({ data, severityFieldName: fieldQuery, key: 'value' }),
      usage,
      showTotals: true,
    },
    isEnabled:
      isEnabled &&
      isListFilterMode(mode) &&
      open &&
      !isDateField &&
      !isBooleanField &&
      supportedType &&
      !isNumberField &&
      isSchemaField &&
      !enableQueryObj,
  };

  const asyncFilterOptionsMemo = useMemo(
    () =>
      asyncGetOptions
        ? {
            options: asyncGetOptions?.(escapedValue),
            isEnabled: open,
          }
        : undefined,
    [asyncGetOptions, escapedValue, open]
  );
  const {
    data: filterOptions = emptyArray,
    isLoading: isLoadingOptions,
    totalRowCount,
  } = useAsyncFilterOptions(asyncFilterOptionsMemo || (enableQueryObj ? queryObjectOptions : querySqlOptions));

  const filteredExtraOptions = useMemo(
    () =>
      extraOptions?.filter(v => !debouncedValue || v.toLowerCase().includes(debouncedValue.toLowerCase())).map(v => ({ value: v })) ?? [],
    [extraOptions, debouncedValue]
  );

  const memoOptions = useMemo(
    () =>
      options
        ? orderByKey(
            options.filter(o => (isBooleanField ? true : (o.value as string)?.toLowerCase?.().includes(search.toLowerCase()))),
            'title'
          )
        : uniqBy(
            getOptions([...filterOptions, ...filteredExtraOptions, ...((value as unknown[]).map?.(generateOption) || [])], label, getValue)
              .filter(({ value }) => !excludeNullFromList || value !== null)
              .map(({ title, value }) => ({
                value,
                title: value === null ? 'Empty' : FILTER_FIELD_LABEL_MAPPINGS[field]?.[value as string] || title,
              })),
            f => f.value
          ),
    [filterOptions, filteredExtraOptions, search, options, isBooleanField, value, label, getOptions, getValue, excludeNullFromList, field]
  );

  const calcOptions = field === 'finding.state' ? findingStateOptions : isBooleanField && isSchemaField ? booleanOptions : memoOptions;

  return {
    measurementsList,
    typeMap,
    calcOptions,
    isLoadingOptions,
    totalRowCount,
    whereClause,
  };
};
